'use client';

import { reportClientError } from '@/lib/reportClientError';
import { recoverCurrentPage } from '@/lib/recoverToHome';
import { useEffect } from 'react';

function isChunkLoadErrorMessage(message: string): boolean {
  return /Loading chunk \d+ failed/i.test(message) || /ChunkLoadError/i.test(message);
}

function isNextRouterStateErrorMessage(message: string): boolean {
  return (
    /parallelRoutes\.get/i.test(message) ||
    /Cannot read properties of null \(reading 'get'\)/i.test(message) ||
    /null is not an object \(evaluating 't\.parallelRoutes\.get'\)/i.test(message)
  );
}

function isWebpackRuntimeStaleChunkMessage(message: string, stack?: string): boolean {
  const s = `${message}\n${stack || ''}`;
  return (
    /Cannot read properties of undefined \(reading 'call'\)/i.test(message) &&
    /_next\/static\/chunks\/webpack|webpack-/i.test(s)
  );
}

function isBenignBrowserNoiseMessage(message: string): boolean {
  // Safari/WebKit: not a real app failure; spams error reporting.
  if (/ResizeObserver loop completed with undelivered notifications/i.test(message)) return true;
  // Some environments/extensions/3P scripts try to register SW and get HTML/blocked; not actionable for us.
  if (
    /service-worker\.js/i.test(message) &&
    /unsupported MIME type|unknown error occurred when fetching/i.test(message)
  ) {
    return true;
  }
  return false;
}

/** Third-party loaders (e.g. TikTok pixel identify) — recovering the whole site does not fix these. */
function isThirdPartyChunkNoise(message: string, stack?: string): boolean {
  const s = `${message}\n${stack || ''}`;
  return /identify\.js/i.test(s) && /tiktok|analytics\.tiktok\.com/i.test(s);
}

function resourceTagFromElement(
  el: HTMLScriptElement | HTMLLinkElement | HTMLImageElement | HTMLIFrameElement,
): 'script' | 'link' | 'img' | 'iframe' | 'unknown' {
  if (el instanceof HTMLScriptElement) return 'script';
  if (el instanceof HTMLLinkElement) return 'link';
  if (el instanceof HTMLImageElement) return 'img';
  if (el instanceof HTMLIFrameElement) return 'iframe';
  return 'unknown';
}

export default function ClientRuntimeGuard() {
  useEffect(() => {
    const resourceDedupe = new Map<string, number>();
    const RESOURCE_TTL_MS = 5 * 60 * 1000;

    const shouldIgnoreResource = (tag: string, resourceUrl: string): boolean => {
      if (!resourceUrl) return false;
      let host = '';
      try {
        host = new URL(resourceUrl).hostname || '';
      } catch {
        host = '';
      }

      // Bing UET pixel is extremely noisy when blocked; skip img beacons entirely.
      if (tag === 'img' && (host === 'bat.bing.com' || host === 'bat.bing.net')) return true;

      return false;
    };

    const shouldDedupeResource = (tag: string, resourceUrl: string): boolean => {
      if (!resourceUrl) return false;
      const now = Date.now();
      const key = `${tag}::${resourceUrl}`;
      const last = resourceDedupe.get(key) || 0;
      if (now - last < RESOURCE_TTL_MS) return true;
      resourceDedupe.set(key, now);
      return false;
    };

    /** Script/link/img/iframe yüklenemezse — genelde 3rd party pixel / CDN; recovery tetiklenmez */
    const onResourceErrorCapture = (event: Event) => {
      const t = event.target;
      if (
        !(t instanceof HTMLScriptElement) &&
        !(t instanceof HTMLLinkElement) &&
        !(t instanceof HTMLImageElement) &&
        !(t instanceof HTMLIFrameElement)
      ) {
        return;
      }
      const ee = event as ErrorEvent;
      if (ee.error != null) {
        return;
      }

      let resourceUrl = '';
      if (t instanceof HTMLScriptElement) resourceUrl = t.src || '';
      else if (t instanceof HTMLLinkElement) resourceUrl = t.href || '';
      else if (t instanceof HTMLImageElement) resourceUrl = t.src || '';
      else if (t instanceof HTMLIFrameElement) resourceUrl = t.src || '';

      const tag = resourceTagFromElement(t);
      if (shouldIgnoreResource(tag, resourceUrl)) return;
      if (shouldDedupeResource(tag, resourceUrl)) return;

      const err = new Error(`Resource failed to load (${resourceTagFromElement(t)})`);
      err.name = 'ResourceLoadError';

      reportClientError({
        boundary: 'global',
        error: err as Error & { digest?: string },
        resourceUrl,
        resourceTag: tag,
      });
    };

    const onError = (event: ErrorEvent) => {
      const et = event.target;
      if (
        (et instanceof HTMLScriptElement ||
          et instanceof HTMLLinkElement ||
          et instanceof HTMLImageElement ||
          et instanceof HTMLIFrameElement) &&
        event.error == null
      ) {
        return;
      }

      const err = (event.error as Error) || new Error(event.message || 'Unknown error');
      const msg = String(event.message || err.message || '');
      if (isBenignBrowserNoiseMessage(msg)) {
        return;
      }
      reportClientError({
        boundary: 'global',
        error: err,
        errorEvent: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          type: event.type,
        },
      });

      // Production-only: auto-recover reloads the page. In local `next dev`, HMR/rebuilds
      // frequently produce webpack "reading 'call'" noise — recovering creates a refresh loop.
      if (process.env.NODE_ENV === 'production') {
        if (
          (isChunkLoadErrorMessage(msg) ||
            isWebpackRuntimeStaleChunkMessage(msg, (err as Error)?.stack)) &&
          !isThirdPartyChunkNoise(msg, (err as Error)?.stack)
        ) {
          recoverCurrentPage('chunk-load');
        }
        if (isNextRouterStateErrorMessage(msg)) recoverCurrentPage('next-router-state');
      }
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as any;
      const err =
        reason instanceof Error
          ? reason
          : new Error(typeof reason === 'string' ? reason : 'Unhandled promise rejection');

      const msg = String(err.message || '');
      if (isBenignBrowserNoiseMessage(msg)) {
        return;
      }
      reportClientError({ boundary: 'global', error: err });

      if (process.env.NODE_ENV === 'production') {
        if (
          (isChunkLoadErrorMessage(msg) ||
            isWebpackRuntimeStaleChunkMessage(msg, err instanceof Error ? err.stack : '')) &&
          !isThirdPartyChunkNoise(msg, err instanceof Error ? err.stack : '')
        ) {
          recoverCurrentPage('chunk-load');
        }
        if (isNextRouterStateErrorMessage(msg)) recoverCurrentPage('next-router-state');
      }
    };

    window.addEventListener('error', onResourceErrorCapture, true);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onResourceErrorCapture, true);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
